import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LISBON_IMAGE } from "../assets/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  full_name: "",
  email: "",
  phone: "",
  company: "",
  role: "",
  dietary: "",
  bring_guest: false,
  guest_name: "",
  message: "",
  billing_nif: "",
  billing_address: "",
  billing_company_nif: "",
};

export default function PresentationSept2026() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(empty);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMeal, setHasMeal] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events/apresentacao-setembro-2026`)
      .then((r) => setHasMeal(!!r.data?.has_meal))
      .catch((e) => console.warn("[PresentationSept2026] event load failed:", e?.message));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/events/apresentacao-setembro-2026/rsvp`, { ...form, language: lang });
      const rsvp = r.data;
      // If event has a price, redirect to Stripe Checkout
      try {
        const info = await axios.get(`${API}/events/apresentacao-setembro-2026/info`);
        if (info.data.price_eur > 0) {
          const checkout = await axios.post(`${API}/payments/checkout/${rsvp.id}`, {
            origin_url: window.location.origin,
          });
          if (checkout.data.url) {
            window.location.href = checkout.data.url;
            return;
          }
        }
      } catch (paymentErr) {
        // If payment setup fails, still show success — data is saved
      }
      setSubmitted(true);
      window.scrollTo({ top: document.getElementById("rsvp-anchor").offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      toast.error("Erro ao registar inscrição.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="presentation-page">
      <section className="relative overflow-hidden bg-[hsl(var(--primary))]">
        <div className="absolute inset-0 opacity-25">
          <img src={LISBON_IMAGE} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative container-page pt-24 pb-24 sm:pt-32 sm:pb-32 text-white">
          <div className="overline text-white/85">{t.presentation.overline}</div>
          <h1 className="display-heading text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.05] max-w-4xl">
            {t.presentation.title}
          </h1>
          <p className="mt-8 text-lg text-white/85 max-w-2xl leading-relaxed">{t.presentation.lead}</p>
        </div>
      </section>

      <section className="container-page py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.presentation.details.map((d, i) => (
            <div key={i} className="border-t-2 border-[hsl(var(--accent))] pt-5">
              <div className="overline">{d.label}</div>
              <div className="mt-3 text-[hsl(var(--foreground))] text-lg display-heading">{d.value}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[hsl(var(--secondary))] py-24" id="rsvp-anchor">
        <div className="container-narrow">
          <div className="overline">{t.presentation.formTitle}</div>
          <h2 className="display-heading text-4xl sm:text-5xl mt-4">{t.presentation.formTitle}</h2>
          <span className="accent-rule mt-6 block" />
          <p className="mt-6 text-[hsl(var(--muted-foreground))] leading-relaxed">{t.presentation.formIntro}</p>

          {submitted ? (
            <div
              className="mt-10 bg-white border border-[hsl(var(--primary))] p-10"
              data-testid="rsvp-success"
            >
              <CheckCircle2 className="h-10 w-10 text-[hsl(var(--primary))]" />
              <h3 className="display-heading text-3xl mt-5 text-[hsl(var(--primary))]">
                {t.presentation.successTitle}
              </h3>
              <p className="mt-4 text-[hsl(var(--muted-foreground))] leading-relaxed">
                {t.presentation.successBody}
              </p>
            </div>
          ) : (
            <form className="mt-10 bg-white border border-[hsl(var(--border))] p-8 sm:p-10 space-y-6" onSubmit={submit} data-testid="rsvp-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label={t.presentation.fields.fullName} required>
                  <input
                    required
                    value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    className="form-input"
                    data-testid="rsvp-fullname"
                  />
                </Field>
                <Field label={t.presentation.fields.email} required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="form-input"
                    data-testid="rsvp-email"
                  />
                </Field>
                <Field label={`${t.presentation.fields.phone} ${t.common.optional}`}>
                  <input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className="form-input"
                    data-testid="rsvp-phone"
                  />
                </Field>
                <Field label={`${t.presentation.fields.company} ${t.common.optional}`}>
                  <input
                    value={form.company}
                    onChange={(e) => set("company", e.target.value)}
                    className="form-input"
                    data-testid="rsvp-company"
                  />
                </Field>
                <Field label={`${t.presentation.fields.role} ${t.common.optional}`}>
                  <input
                    value={form.role}
                    onChange={(e) => set("role", e.target.value)}
                    className="form-input"
                    data-testid="rsvp-role"
                  />
                </Field>
                {hasMeal && (
                  <Field label={`${t.presentation.fields.dietary} ${t.common.optional}`}>
                    <input
                      value={form.dietary}
                      onChange={(e) => set("dietary", e.target.value)}
                      className="form-input"
                      data-testid="rsvp-dietary"
                    />
                  </Field>
                )}
              </div>

              <div className="border-t border-[hsl(var(--border))] pt-6">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="rsvp-guest-toggle">
                  <input
                    type="checkbox"
                    checked={form.bring_guest}
                    onChange={(e) => set("bring_guest", e.target.checked)}
                    className="mt-1 accent-[hsl(var(--primary))]"
                  />
                  <span className="text-sm">{t.presentation.fields.bringGuest}</span>
                </label>
                {form.bring_guest && (
                  <div className="mt-4">
                    <Field label={t.presentation.fields.guestName}>
                      <input
                        value={form.guest_name}
                        onChange={(e) => set("guest_name", e.target.value)}
                        className="form-input"
                        data-testid="rsvp-guest-name"
                      />
                    </Field>
                  </div>
                )}
              </div>

              <Field label={t.presentation.fields.message}>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className="form-input resize-none"
                  data-testid="rsvp-message"
                />
              </Field>

              <details className="border-t border-[hsl(var(--border))] pt-6 group" data-testid="rsvp-billing-section">
                <summary className="cursor-pointer list-none flex items-center justify-between text-sm text-[hsl(var(--primary))] font-medium">
                  <span>Dados para Declaração de Donativo <span className="text-[hsl(var(--muted-foreground))]">(opcional, para contabilidade)</span></span>
                  <span className="text-xs transition-transform group-open:rotate-180">▼</span>
                </summary>
                <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
                  A Associação CIEES Portugal não emite faturas. Se precisar de uma <strong>Declaração de Donativo</strong> para efeitos contabilísticos, preencha estes dados. Receberá um PDF em anexo após o pagamento.
                </p>
                <div className="mt-5 grid sm:grid-cols-2 gap-6">
                  <Field label="NIF">
                    <input
                      value={form.billing_nif}
                      onChange={(e) => set("billing_nif", e.target.value)}
                      className="form-input"
                      placeholder="NIF pessoal"
                      data-testid="rsvp-billing-nif"
                    />
                  </Field>
                  <Field label="NIF da empresa (se aplicável)">
                    <input
                      value={form.billing_company_nif}
                      onChange={(e) => set("billing_company_nif", e.target.value)}
                      className="form-input"
                      placeholder="NIF empresa"
                      data-testid="rsvp-billing-company-nif"
                    />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Morada fiscal">
                    <input
                      value={form.billing_address}
                      onChange={(e) => set("billing_address", e.target.value)}
                      className="form-input"
                      placeholder="Rua, número, código postal, localidade"
                      data-testid="rsvp-billing-address"
                    />
                  </Field>
                </div>
              </details>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary disabled:opacity-60"
                data-testid="rsvp-submit-button"
              >
                {loading ? t.common.sending : t.common.register}
              </button>
            </form>
          )}
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: white;
          border: 1px solid hsl(var(--border));
          padding: 10px 12px;
          font-family: "Outfit", sans-serif;
          font-size: 15px;
          color: hsl(var(--foreground));
        }
        .form-input:focus { outline: none; border-color: hsl(var(--accent)); }
      `}</style>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">
        {label} {required && <span className="text-[hsl(var(--accent))]">*</span>}
      </label>
      {children}
    </div>
  );
}
